import { format } from "date-fns";
import { SessionEventType, type SessionEvent } from "@mycompbox/shared";
import { SectionCard } from "@/components/new-look/blocks";
import type { EpicRequest, PlanningState } from "@/lib/api/epic-requests";
import type { Epic } from "@/lib/api/types";
import { cn } from "@/lib/utils";

/**
 * "How it grew" (docs/new-look/PLAN.md, step 4.8): the idea's milestones from
 * first line to shipped, oldest first.
 */

export interface Milestone {
  at: string;
  text: string;
  tone: "plain" | "ok" | "muted";
}

function planningOf(idea: EpicRequest): PlanningState | null {
  const raw = idea.planningState;
  if (!raw) return null;
  if (typeof raw !== "string") return raw;
  try {
    return JSON.parse(raw) as PlanningState;
  } catch {
    return null;
  }
}

function named(event: SessionEvent): string {
  const p = event.payload as Record<string, unknown> | undefined;
  return [p?.identifier, p?.title]
    .filter((v): v is string => typeof v === "string" && v.length > 0)
    .join(": ");
}

export function milestones(
  idea: EpicRequest,
  epic: Pick<Epic, "createdAt" | "implementedDate"> | null,
  events: readonly SessionEvent[]
): Milestone[] {
  const out: Milestone[] = [
    {
      at: idea.createdAt,
      text: `Idea saved${idea.requestedBy?.name ? ` by ${idea.requestedBy.name}` : ""}`,
      tone: "plain",
    },
  ];
  const planning = planningOf(idea);
  const started = planning?.stages
    .map((s) => s.startedAt)
    .filter((v): v is string => !!v)
    .sort()[0];
  if (started)
    out.push({ at: started, text: "The Blueprint maker started planning", tone: "plain" });
  if (epic) out.push({ at: epic.createdAt, text: "Blueprint made", tone: "ok" });

  const firstSession = [...events]
    .filter((e) => e.eventType === SessionEventType.SESSION_STARTED)
    .sort((a, b) => a.timestamp.localeCompare(b.timestamp))[0];
  if (firstSession)
    out.push({ at: firstSession.timestamp, text: "Building started", tone: "plain" });
  for (const e of events) {
    if (e.eventType === SessionEventType.SESSION_FEATURE_COMPLETED)
      out.push({ at: e.timestamp, text: `Part done: ${named(e)}`, tone: "ok" });
  }
  if (epic?.implementedDate) out.push({ at: epic.implementedDate, text: "Shipped", tone: "ok" });
  if (idea.status === "rejected")
    out.push({ at: idea.updatedAt, text: "Turned down", tone: "muted" });

  return out
    .filter((m) => !Number.isNaN(new Date(m.at).getTime()))
    .sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
}

export function IdeaHistory({ items }: { items: readonly Milestone[] }) {
  return (
    <SectionCard title="Timeline">
      <ol className="relative flex flex-col gap-3 border-l border-border pl-5">
        {items.map((m, i) => (
          <li key={`${m.at}:${String(i)}`} className="relative">
            <span
              aria-hidden="true"
              className={cn(
                "absolute -left-[26px] top-1.5 h-2.5 w-2.5 rounded-full ring-4 ring-background",
                m.tone === "ok"
                  ? "bg-green-600 dark:bg-green-400"
                  : m.tone === "muted"
                    ? "bg-muted-foreground"
                    : "bg-primary"
              )}
            />
            <p className="text-sm text-foreground">{m.text}</p>
            <time dateTime={m.at} className="text-xs text-muted-foreground">
              {format(new Date(m.at), "d MMM, HH:mm")}
            </time>
          </li>
        ))}
      </ol>
    </SectionCard>
  );
}
